import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  MapPinIcon,
  Squares2X2Icon,
  MapIcon,
  PlusIcon,
  ClipboardDocumentCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { EmptyState } from '../components/common/EmptyState';
import { PropertyMap } from '../components/properties/PropertyMap';
import { createProperty, fetchProperties, fetchInspections } from '../lib/apiClient';
import type { Property, Inspection } from '../types';
import { formatDistanceToNow } from '../lib/time';
import { useAuth } from '../lib/auth';

type ViewMode = 'grid' | 'map';

interface AddressFormState {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

interface PropertyFormState {
  name: string;
  referenceCode: string;
  address: AddressFormState;
}

const initialAddress: AddressFormState = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  postcode: '',
  country: 'Australia',
};

const initialFormState: PropertyFormState = {
  name: '',
  referenceCode: '',
  address: initialAddress,
};

export function PropertiesPage(): JSX.Element {
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [form, setForm] = useState<PropertyFormState>(initialFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: properties = [], isLoading } = useQuery<Property[]>({
    queryKey: ['properties', user?.organizationId],
    queryFn: fetchProperties,
    enabled: Boolean(user),
  });

  const { data: inspections = [] } = useQuery<Inspection[]>({
    queryKey: ['inspections', user?.organizationId],
    queryFn: () => fetchInspections({}),
    enabled: Boolean(user),
  });

  const mutation = useMutation({
    mutationFn: createProperty,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['properties', user?.organizationId] });
      setForm(initialFormState);
      setShowForm(false);
      setFormError(null);
    },
    onError: (error: unknown) => {
      setFormError(error instanceof Error ? error.message : 'Failed to create property. Please try again.');
    },
  });

  const sortedProperties = useMemo(() => {
    return [...properties].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [properties]);

  // Count inspections per property
  const inspectionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    inspections.forEach((inspection) => {
      const propertyId = typeof inspection.propertyId === 'string' 
        ? inspection.propertyId 
        : inspection.propertyId?._id;
      if (propertyId) {
        counts[propertyId] = (counts[propertyId] || 0) + 1;
      }
    });
    return counts;
  }, [inspections]);

  const getPropertyInitials = (name: string): string => {
    const words = name.split(' ');
    return words.slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
  };

  const getPropertyColor = (id: string): string => {
    const colors = [
      'from-blue-500/20 to-blue-600/10',
      'from-emerald-500/20 to-emerald-600/10',
      'from-purple-500/20 to-purple-600/10',
      'from-amber-500/20 to-amber-600/10',
      'from-rose-500/20 to-rose-600/10',
      'from-cyan-500/20 to-cyan-600/10',
    ];
    const index = id.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const handleAddressChange = (key: keyof AddressFormState, value: string) => {
    setForm((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [key]: value,
      },
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    if (!form.name.trim()) {
      setFormError('Property name is required.');
      return;
    }
    if (!form.address.line1.trim() || !form.address.city.trim() || !form.address.state.trim() || !form.address.postcode.trim()) {
      setFormError('Complete the primary address fields before saving.');
      return;
    }

    mutation.mutate({
      name: form.name.trim(),
      referenceCode: form.referenceCode.trim() || undefined,
      address: {
        line1: form.address.line1.trim(),
        line2: form.address.line2.trim() || undefined,
        city: form.address.city.trim(),
        state: form.address.state.trim(),
        postcode: form.address.postcode.trim(),
        country: form.address.country.trim() || 'Australia',
      },
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setForm(initialFormState);
    setFormError(null);
    mutation.reset();
  };

  const openForm = () => {
    setShowForm(true);
    setFormError(null);
    mutation.reset();
  };

  return (
    <div className="space-y-6">
      <header className="glass-card flex flex-wrap items-center justify-between gap-4 p-6">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Properties</h1>
          <p className="text-sm text-muted-foreground">Manage your portfolio to unlock inspection workflows.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-xl border border-border bg-white/50 p-1 dark:bg-white/5">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary text-white'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Squares2X2Icon className="h-3.5 w-3.5" />
              Grid
            </button>
            <button
              type="button"
              onClick={() => setViewMode('map')}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                viewMode === 'map'
                  ? 'bg-primary text-white'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <MapIcon className="h-3.5 w-3.5" />
              Map
            </button>
          </div>
          <button
            type="button"
            className="btn-glass btn-primary focus-ring flex items-center gap-1.5 text-xs font-semibold"
            onClick={openForm}
            data-onboarding="property-create"
          >
            <PlusIcon className="h-4 w-4" />
            Add property
          </button>
        </div>
      </header>

      {/* Add Property Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="glass-card max-h-[90vh] w-full max-w-lg overflow-y-auto p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground">New property</h2>
                <p className="text-sm text-muted-foreground">Add a new property to your portfolio.</p>
              </div>
              <button
                type="button"
                className="focus-ring rounded-full p-2 hover:bg-white/20"
                onClick={handleCancel}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="space-y-2 text-sm font-medium text-foreground">
                  Property name
                  <input
                    type="text"
                    value={form.name}
                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                    className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
                    placeholder="e.g. 23 Ocean View"
                    required
                  />
                </label>
                <label className="space-y-2 text-sm font-medium text-foreground">
                  Reference code
                  <input
                    type="text"
                    value={form.referenceCode}
                    onChange={(event) => setForm((prev) => ({ ...prev, referenceCode: event.target.value }))}
                    className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
                    placeholder="Optional"
                  />
                </label>
              </div>

              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Address</p>
                <input
                  type="text"
                  value={form.address.line1}
                  onChange={(event) => handleAddressChange('line1', event.target.value)}
                  className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
                  placeholder="Street address"
                  required
                />
                <input
                  type="text"
                  value={form.address.line2}
                  onChange={(event) => handleAddressChange('line2', event.target.value)}
                  className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
                  placeholder="Apartment, suite, etc. (optional)"
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <input
                    type="text"
                    value={form.address.city}
                    onChange={(event) => handleAddressChange('city', event.target.value)}
                    className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
                    placeholder="City"
                    required
                  />
                  <input
                    type="text"
                    value={form.address.state}
                    onChange={(event) => handleAddressChange('state', event.target.value)}
                    className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
                    placeholder="State"
                    required
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input
                    type="text"
                    value={form.address.postcode}
                    onChange={(event) => handleAddressChange('postcode', event.target.value)}
                    className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
                    placeholder="Postcode"
                    required
                  />
                  <input
                    type="text"
                    value={form.address.country}
                    onChange={(event) => handleAddressChange('country', event.target.value)}
                    className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
                    placeholder="Country"
                  />
                </div>
              </div>

              {formError && (
                <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400">
                  {formError}
                </p>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="btn-glass btn-secondary focus-ring text-sm font-semibold"
                  onClick={handleCancel}
                  disabled={mutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-glass btn-primary focus-ring text-sm font-semibold disabled:opacity-50"
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? 'Saving…' : 'Save property'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="glass-card h-64 animate-pulse" />
          ))}
        </div>
      ) : sortedProperties.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {sortedProperties.map((property) => (
              <article
                key={property._id}
                className="glass-card group flex flex-col overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5"
              >
                {/* Property Image / Initials Header */}
                <div className={`relative h-36 bg-gradient-to-br ${getPropertyColor(property._id)}`}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-white/90 text-3xl font-bold text-primary shadow-xl dark:bg-gray-800">
                      {getPropertyInitials(property.name)}
                    </div>
                  </div>
                  {property.referenceCode && (
                    <span className="absolute right-3 top-3 rounded-full bg-black/20 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm">
                      {property.referenceCode}
                    </span>
                  )}
                </div>

                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-semibold text-foreground line-clamp-1 group-hover:text-primary transition-colors">
                    {property.name}
                  </h3>
                  
                  <div className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
                    <MapPinIcon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span className="line-clamp-2">
                      {property.address.line1}
                      {property.address.line2 ? `, ${property.address.line2}` : ''}<br />
                      {property.address.city}, {property.address.state} {property.address.postcode}
                    </span>
                  </div>

                  <div className="mt-auto flex items-center justify-between pt-4">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <ClipboardDocumentCheckIcon className="h-3.5 w-3.5" />
                        {inspectionCounts[property._id] || 0} inspections
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/app/items?propertyId=${property._id}`)}
                      className="btn-glass btn-secondary focus-ring text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      View
                    </button>
                  </div>

                  <p className="mt-2 text-xs text-muted-foreground">
                    Updated {formatDistanceToNow(property.updatedAt)}
                  </p>
                </div>
              </article>
            ))}
          </div>
        ) : (
          /* Map View */
          <div className="glass-card relative h-[600px] overflow-hidden">
            <PropertyMap 
              properties={sortedProperties} 
              onPropertyClick={(property) => navigate(`/app/items?propertyId=${property._id}`)}
            />
            
            {/* Property List Sidebar in Map View */}
            <div className="absolute bottom-4 left-4 right-4 max-h-48 overflow-y-auto rounded-xl bg-white/95 p-3 shadow-lg backdrop-blur-sm dark:bg-gray-900/95">
              <div className="flex gap-3 overflow-x-auto pb-2">
                {sortedProperties.slice(0, 6).map((property) => (
                  <button
                    key={property._id}
                    type="button"
                    onClick={() => navigate(`/app/items?propertyId=${property._id}`)}
                    className="flex min-w-[200px] items-center gap-3 rounded-lg border border-border bg-white p-3 text-left transition-shadow hover:shadow-md dark:bg-gray-800"
                  >
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${getPropertyColor(property._id)} text-sm font-bold text-primary`}>
                      {getPropertyInitials(property.name)}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{property.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{property.address.city}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )
      ) : (
        <EmptyState
          title="No properties yet"
          description="Add your first property to start creating inspections and AI-powered reports."
          actionLabel="Add property"
          onAction={openForm}
        />
      )}
    </div>
  );
}
